import type {
  LeaderboardHoleScoreFragment,
  PlayerSummaryFragment,
} from '../../__generated__/hooks'
import DetailSheet from './DetailSheet'
import MiniScorecard, { type CourseHoleSummary } from './MiniScorecard'

/**
 * Team detail sheet — Scramble and BestBall use this for team rows.
 * Hero shows team name with members listed in subtitle, primary stat
 * = score, secondary = points + strokes. The scorecard renders the
 * team ball (one score per hole, not per-member).
 *
 * Shape decoupled from the underlying fragment because Scramble +
 * BestBall team entries differ slightly and the caller knows how to
 * pluck the right fields out.
 */
const TeamDetail = ({
  open,
  onClose,
  data,
  courseHoles,
}: {
  open: boolean
  onClose: () => void
  data: {
    rank?: number | null
    teamName: string
    flightNumber: number | null | undefined
    members: readonly PlayerSummaryFragment[]
    score: number
    strokes: number
    strokesNet: number
    points: number
    holesRegistred: number
    /**
     * Hole-scores the format expects, when that is not one per hole. A split
     * round's singles block is played 1v1 and 1v1, so both partners' balls
     * count and "24 spilte hull" would misname 18 holes of golf.
     */
    holesTotal?: number
    /**
     * What each block contributed. Only a split round has one — its total is
     * the three added, over 24 hole-scores, so the parts are the explanation
     * rather than a detail.
     */
    breakdown?: readonly {
      ordinal: number
      label: string
      holes: string | null
      value: number
    }[]
    scores: readonly LeaderboardHoleScoreFragment[]
  } | null
  courseHoles: readonly CourseHoleSummary[]
}) => {
  if (!data) return null
  let formattedScore: string
  if (data.score === 0) formattedScore = 'E'
  else if (data.score > 0) formattedScore = `+${data.score}`
  else formattedScore = String(data.score)
  const strokes = data.strokesNet || data.strokes
  const memberList = data.members.map((m) => m.name).join(', ')
  const subtitleParts: string[] = [
    data.holesTotal != null && data.holesTotal !== 18
      ? `${data.holesRegistred} av ${data.holesTotal} delresultater`
      : `${data.holesRegistred} spilte hull`,
  ]
  if (data.flightNumber != null) {
    subtitleParts.push(`flight #${data.flightNumber}`)
  }
  if (memberList) subtitleParts.push(memberList)
  return (
    <DetailSheet
      open={open}
      onClose={onClose}
      rank={data.rank ?? null}
      title={data.teamName}
      subtitle={subtitleParts.join(' · ')}
      primary={formattedScore}
      secondary={`${data.points} pts · ${strokes} slag`}
    >
      {data.breakdown?.length ? (
        // Above the card, because on a split round this IS the result — the
        // total is the three blocks added, over 24 hole-scores rather than a
        // round, so the parts explain the number the board ranked on.
        <section
          aria-label="Etapper"
          className="mb-2 rounded-xl bg-paper shadow-sm overflow-hidden"
        >
          <div className="px-3 pt-3 pb-1 up text-ink-500 text-[10px]">
            Etapper
          </div>
          <ul className="px-3 pb-2">
            {data.breakdown.map((b) => (
              <li
                key={b.ordinal}
                className="flex items-baseline justify-between gap-2 py-1 border-b border-ink-100 last:border-b-0"
              >
                <span className="text-sm text-ink-900">{b.label}</span>
                {b.holes ? (
                  <span className="flex-1 font-mono num text-[11px] text-ink-500">
                    hull {b.holes}
                  </span>
                ) : null}
                <span className="font-mono num text-sm font-semibold text-ink-900">
                  {b.value}
                </span>
              </li>
            ))}
            <li className="flex items-baseline justify-between gap-2 pt-1.5">
              <span className="text-sm font-semibold text-ink-900">Totalt</span>
              <span className="font-mono num text-sm font-bold text-ink-900">
                {data.breakdown.reduce((acc, b) => acc + b.value, 0)}
              </span>
            </li>
          </ul>
        </section>
      ) : null}
      {data.breakdown?.length ? null : ( // three cards properly. // made of. A player's own holes live on the scorecard, which shows all // The block figures above ARE the detail: they are what the total is // // up, and it printed 9 on a par 4 where partners shot 4 and 5. // singles balls into one number was the only way to make the grid add // honestly here than on the full scorecard. Summing a hole's two // eighteen cells cannot hold three coordinate systems any more // cards — one ball for the team, the better of two, then both — and // No hole grid for a split round. Its holes belong to three different
        <section className="rounded-xl bg-paper shadow-sm overflow-hidden">
          <div className="px-3 pt-3 pb-1 up text-ink-500 text-[10px]">
            Scorekort
          </div>
          <MiniScorecard courseHoles={courseHoles} scores={data.scores} />
        </section>
      )}
    </DetailSheet>
  )
}

export default TeamDetail
